// An availability run on the run page: what it did, step by step. It calls no platform, so
// the cart panels (stores, proxies, churn) have nothing to say about it.
import { Link } from "react-router-dom";
import { type CartProgress, fmtDuration, fmtInt, fmtRows, stepLabel, stepsInOrder } from "../api/labels";
import { Badge, Dot, Grid, Panel } from "@akaike/ui";
import { Stat } from "./foundation";

export function AvailabilityRunPanel({
  stats,
  live,
  elapsed,
}: {
  stats: CartProgress;
  live: boolean;
  elapsed: number | null;
}) {
  const ms = stats.step_ms ?? {};
  const rows = stats.step_rows ?? {};
  const finished = stepsInOrder(ms);
  return (
    <>
      <Grid cols={4} gap="md" className="mb-3.5">
        <Stat
          surface
          label="runs enriched"
          value={fmtInt(stats.runs_enriched)}
          tone="ok"
          foot={<span className="faint">{fmtRows(stats.rows_emitted)} fact rows</span>}
        />
        <Stat
          surface
          label="Reach built"
          value={fmtInt(stats.runs_reach_built)}
          tone={(stats.reach_failed ?? 0) > 0 ? "warn" : undefined}
          foot={
            <span className="faint">
              {stats.runs_without_reach != null &&
                `of ${fmtInt(stats.runs_without_reach)} missing (${fmtInt(stats.skus_without_reach)} SKUs), `}
              {fmtInt(stats.reach_failed ?? 0)} failed
            </span>
          }
        />
        <Stat
          surface
          label={<>{live && <Dot live />} ClickHouse rows read</>}
          value={fmtRows(stats.ch_rows_read)}
          foot={<span className="faint">{live && stats.step ? `in ${stepLabel(stats.step)}` : "this run"}</span>}
        />
        <Stat
          surface
          label="run time"
          value={fmtDuration(elapsed)}
          foot={
            <span className="faint">
              {stats.day ? `day ${stats.day}` : `${fmtInt(stats.days_rolled_up)} day(s) rolled up`}
              {stats.remaining_days != null && ` - ${stats.remaining_days} day(s) to go`}
            </span>
          }
        />
      </Grid>
      <Grid cols={12} gap="md" className="mb-3.5 items-start">
        <Panel style={{ gridColumn: "span 7" }}>
          <Panel.Head title="Steps">
            {stats.noop && <Badge variant="outline" className="ml-2.5">nothing to do</Badge>}
            <div className="flex-1" />
            <Link to="/availability" className="text-[12px]">Availability pipeline</Link>
          </Panel.Head>
          <Panel.Body tight>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Step</th>
                  <th className="r">Time</th>
                  <th className="r">Rows</th>
                </tr>
              </thead>
              <tbody>
                {finished.map((step) => (
                  <tr key={step}>
                    <td>{stepLabel(step)}</td>
                    <td className="r num">{fmtDuration(ms[step] / 1000)}</td>
                    <td className="r num">{fmtInt(rows[step])}</td>
                  </tr>
                ))}
                {live && stats.step && !(stats.step in ms) && (
                  <tr>
                    <td>
                      <Dot live /> {stepLabel(stats.step)}
                    </td>
                    <td className="r faint" colSpan={2}>running</td>
                  </tr>
                )}
                {stats.failed_step && (
                  <tr className="row-crit">
                    <td>{stepLabel(stats.failed_step)}</td>
                    <td className="r" colSpan={2}>failed</td>
                  </tr>
                )}
                {finished.length === 0 && !live && !stats.failed_step && (
                  <tr>
                    <td className="faint" colSpan={3}>No step ran.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel.Body>
        </Panel>
      </Grid>
    </>
  );
}
